<script lang="ts">
  import { Link } from '@inertiajs/svelte';
  import { onDestroy, untrack } from 'svelte';
  import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
  import { toast } from 'svelte-sonner';
  import momentiumLogo from '../../assets/momentium-logo.png';
  import MobileTopBar from '../../components/mobile/MobileTopBar.svelte';
  import DateRangeNav from '../../components/DateRangeNav.svelte';
  import HabitsToday, { type HabitsTodayGroup } from '../../components/mobile/HabitsToday.svelte';
  import SegmentedControl from '../../components/mobile/SegmentedControl.svelte';
  import NavDropdown from '../../components/NavDropdown.svelte';
  import ProfileDropdown from '../../components/ProfileDropdown.svelte';
  import { jsonHeaders, readApiErrorMessage } from '../../lib/apiHelpers';
  import { setCategoryDragImage } from '../../lib/dragImage';
  import { tapAlternatives, viewport } from '../../lib/stores/viewport';
  import ActionSheet from '../../components/mobile/ActionSheet.svelte';
  import Icon from '../../components/Icon.svelte';
  import {
    moveItem,
    moveUpDownActions,
    type MoveDirection,
    type SheetAction,
  } from '../../lib/sheetActions';
  import {
    DEFAULT_COMPLETED_CELL_COLOR,
    habitsViewSettings,
    updateHabitsViewSettings,
  } from '../../lib/stores/habitsViewSettings';
  import { relativeDayLabel } from '../../lib/dateRangeLabel';
  import { SPARKLINE_DAYS, daysBetween, isScheduledOn, shiftDate } from '../../lib/habits';
  import {
    DEFAULT_HABITS_MODE,
    readHabitsMode,
    saveHabitsMode,
    type HabitsMode,
  } from '../../lib/habitsViewMode';
  import { createLongPressTracker } from '../../lib/longPress';
  import { isDialogOpen, shouldHandleTodayHotkey } from '../../lib/pageHotkeys';
  import { useModalAnimation } from '../../lib/useModalAnimation.svelte';
  import { INFO_POPOVER_MAX_HEIGHT_PX, pickInfoPlacement } from '../../lib/habitInfoPopover';
  import type {
    Habit,
    HabitCategory,
    HabitLog,
    HabitLogStatus,
    HabitsPayload,
  } from '../../lib/types';

  interface Props extends HabitsPayload {}
  let { range, habits, logs, categories, today }: Props = $props();
  let showArchived = $state(false);
  let habitModal = $state(false);
  let manageCategoriesOpen = $state(false);
  let settingsMenuPinned = $state(false);
  let settingsMenuHovered = $state(false);
  let settingsMenuOpen = $derived(settingsMenuPinned || settingsMenuHovered);
  let settingsTriggerEl = $state<HTMLButtonElement | undefined>();
  let viewSettingsOpen = $state(false);
  let editingHabit = $state<Habit | null>(null);
  // Info popover: shows the full habit name + description on hover/focus of the
  // name. Only appears when the row is hiding something (clamped title or a
  // description), and is positioned below the row.
  let truncatedTitles = $state<Record<string, boolean>>({});
  let infoHabitId = $state<string | null>(null);
  let infoOpenTimer: number | undefined;
  let infoCloseTimer: number | undefined;
  const INFO_OPEN_DELAY_MS = 80;
  const INFO_CLOSE_DELAY_MS = 120;
  let infoPlacement = $state<'below' | 'above'>('below');
  let infoMaxHeight = $state<number | undefined>(undefined);
  /** Popover is rendered offscreen and awaits measurement before being revealed. */
  let infoPending = $state(false);
  let popoverEl = $state<HTMLDivElement | undefined>();
  let triggerNodes = $state<Record<string, HTMLButtonElement>>({});
  let boardScrollLeft = $state(0);

  function infoVisibleFor(habit: Habit) {
    return infoHabitId === habit.id && (!!truncatedTitles[habit.id] || !!habit.description);
  }
  function infoEnter(habitId: string) {
    clearTimeout(infoCloseTimer);
    clearTimeout(infoOpenTimer);
    infoOpenTimer = window.setTimeout(() => {
      infoHabitId = habitId;
      infoPlacement = 'below';
      infoMaxHeight = INFO_POPOVER_MAX_HEIGHT_PX;
      // Render the popover offscreen first, measure it against the board
      // container, then reveal it at the right placement — avoids a visible
      // flash and keeps the popover from overflowing the container.
      infoPending = true;
      requestAnimationFrame(() => requestAnimationFrame(() => measureInfoPlacement()));
    }, INFO_OPEN_DELAY_MS);
  }
  function infoLeave(habitId: string) {
    clearTimeout(infoOpenTimer);
    infoCloseTimer = window.setTimeout(() => {
      if (infoHabitId === habitId) infoHabitId = null;
    }, INFO_CLOSE_DELAY_MS);
  }
  function infoPopoverEnter(habitId: string) {
    clearTimeout(infoCloseTimer);
    infoHabitId = habitId;
  }
  function infoPopoverLeave(habitId: string) {
    infoCloseTimer = window.setTimeout(() => {
      if (infoHabitId === habitId) infoHabitId = null;
    }, INFO_CLOSE_DELAY_MS);
  }
  /** Marks a habit as having a two-line-clamped title (scrollHeight > clientHeight). */
  function clampOverflowAction(node: HTMLButtonElement, habitId: string) {
    triggerNodes[habitId] = node;
    const check = () => {
      truncatedTitles[habitId] = node.scrollHeight > node.clientHeight + 1;
    };
    check();
    const raf = requestAnimationFrame(check);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(check);
      observer.observe(node);
    }
    return {
      destroy() {
        cancelAnimationFrame(raf);
        observer?.disconnect();
        delete triggerNodes[habitId];
      },
    };
  }
  /**
   * Measures the row against the `.board-wrap` container and picks the popover
   * placement (below, or above when the bottom would overflow) plus a
   * max-height clamp so the popover never escapes the container.
   */
  function measureInfoPlacement() {
    const id = infoHabitId;
    if (!id) return;
    const trigger = triggerNodes[id];
    if (!trigger) return;
    const row = trigger.closest('.habit-name');
    const wrap = trigger.closest('.board-wrap');
    if (!(row instanceof HTMLElement) || !(wrap instanceof HTMLElement)) return;
    const rowRect = row.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const availableBelow = wrapRect.bottom - rowRect.bottom;
    const availableAbove = rowRect.top - wrapRect.top;
    // Measure the real rendered height. The popover is laid out in place (via
    // the .measuring transform) at its real width, so long descriptions wrap
    // and measure accurately — a fixed left: -9999px measurement would blow
    // the used width up to viewport + 9999px and understate the height.
    const popoverHeight = popoverEl ? popoverEl.getBoundingClientRect().height : undefined;
    const result = pickInfoPlacement({ popoverHeight, availableBelow, availableAbove });
    infoPlacement = result.placement;
    infoMaxHeight = result.maxHeight;
    infoPending = false;
  }
  let saving = $state(false);
  let habitForm = $state({ title: '', description: '', categoryId: '', startDate: '' });
  let habitFormBaseline = '';
  let discardConfirmation = $state<'habit' | 'categories' | null>(null);
  let archiveConfirmation = $state<Habit | null>(null);
  let deleteConfirmation = $state<Habit | null>(null);
  let categoryDeleteConfirmation = $state<HabitCategory | null>(null);
  let habitFormDirty = $derived(habitModal && JSON.stringify(habitForm) !== habitFormBaseline);
  const habitModalAnimation = useModalAnimation(
    () => habitModal,
    () => (habitModal = false),
  );
  const manageCategoriesModal = useModalAnimation(
    () => manageCategoriesOpen,
    () => (manageCategoriesOpen = false),
  );
  const viewSettingsModal = useModalAnimation(
    () => viewSettingsOpen,
    () => (viewSettingsOpen = false),
  );
  const discardModal = useModalAnimation(
    () => discardConfirmation !== null,
    () => (discardConfirmation = null),
  );
  const archiveModal = useModalAnimation(
    () => archiveConfirmation !== null,
    () => (archiveConfirmation = null),
  );
  const deleteModal = useModalAnimation(
    () => deleteConfirmation !== null,
    () => (deleteConfirmation = null),
  );
  const categoryDeleteModal = useModalAnimation(
    () => categoryDeleteConfirmation !== null,
    () => (categoryDeleteConfirmation = null),
  );
  const DEFAULT_CATEGORY_COLOR = '#475569';
  const CATEGORY_COLORS = [
    '#475569',
    '#b91c1c',
    '#c2410c',
    '#b45309',
    '#166534',
    '#0f766e',
    '#1d4ed8',
    '#4338ca',
    '#6d28d9',
    '#be185d',
  ];
  const CATEGORY_COLOR_LABELS: Record<string, string> = {
    '#475569': 'Slate',
    '#b91c1c': 'Red',
    '#c2410c': 'Orange',
    '#b45309': 'Amber',
    '#166534': 'Green',
    '#0f766e': 'Teal',
    '#1d4ed8': 'Blue',
    '#4338ca': 'Indigo',
    '#6d28d9': 'Purple',
    '#be185d': 'Pink',
  };
  const THRESHOLD_OPTIONS = [50, 75, 100];

  // === Category picker (habit form dropdown) ===
  let categoryMenuOpen = $state(false);
  let categoryMenuAnchor: HTMLElement | undefined = $state();
  let categoryMenuEl = $state<HTMLDivElement | undefined>();
  let categoryMenuStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');
  let categoryNewMode = $state(false);
  let categoryNewName = $state('');
  let categoryNewColor = $state(DEFAULT_CATEGORY_COLOR);
  let categorySaving = $state(false);

  // === Manage categories modal ===
  let manageCategoryDrafts = $state<HabitCategory[]>([]);
  let newManageCategoryName = $state('');
  let newManageCategoryColor = $state<string | null>(null);
  let newManageCategoryTagline = $state('');
  let newManageCategoryThreshold = $state(100);
  let manageCategorySaving = $state(false);
  let draggedCategoryId = $state<string | null>(null);
  let draggedHabitId = $state<string | null>(null);
  let manageColorPopoverId = $state<string | null>(null);
  let manageColorAnchor: HTMLElement | undefined = $state();
  let manageColorEl = $state<HTMLDivElement | undefined>();
  let manageColorStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');
  let manageColorPopoverSelected = $derived.by(() => {
    if (manageColorPopoverId === null) return null;
    if (manageColorPopoverId === 'new') return newManageCategoryColor;
    return manageCategoryDrafts.find((entry) => entry.id === manageColorPopoverId)?.color ?? null;
  });
  let colorTooltipText = $state<string | null>(null);
  let colorTooltipPosition = $state<{ x: number; y: number } | null>(null);
  let colorTooltipTimer: ReturnType<typeof setTimeout> | null = null;

  let visibleHabits = $derived(habits.filter((habit) => showArchived || !habit.archivedAt));
  function sortGroupHabits(groupHabits: Habit[]) {
    return [...groupHabits].sort(
      (a, b) =>
        Number(Boolean(a.archivedAt)) - Number(Boolean(b.archivedAt)) ||
        a.position - b.position ||
        a.title.localeCompare(b.title),
    );
  }
  let grouped = $derived.by(() => {
    const groups = new Map<string, Habit[]>();
    for (const habit of visibleHabits) {
      const key = habit.categoryId || 'uncategorized';
      groups.set(key, [...(groups.get(key) || []), habit]);
    }
    return [
      ...categories
        .map((category) => [category.id, sortGroupHabits(groups.get(category.id) || [])] as const)
        .filter(([, groupHabits]) => groupHabits.length > 0),
      ...((groups.get('uncategorized')?.length ?? 0) > 0
        ? ([['uncategorized', sortGroupHabits(groups.get('uncategorized') || [])]] as const)
        : []),
    ];
  });
  // Taps show at once: the pending status overlays the saved logs until the
  // server answers. `id` lets a later tap on the same cell keep its overlay
  // when an earlier request settles.
  let pendingLogs = $state<Record<string, { status: HabitLogStatus | 'unlogged'; id: number }>>({});
  let pendingLogId = 0;
  // The logs from before the loaded range, which Today mode needs for streaks.
  // The server only sends them when asked, so the grid, tablets and desktop
  // never carry them. They belong to the range start they were loaded for: a
  // different window doesn't use them.
  let history = $state<{ rangeStart: string; startDate: string; logs: HabitLog[] } | null>(null);
  let historyLoaded = $derived(history?.rangeStart === range.startDate);
  let logMap = $derived.by(() => {
    const map = new Map<string, HabitLogStatus>(
      [...(historyLoaded ? history!.logs : []), ...logs].map((log) => [
        `${log.habitId}:${log.localDate}`,
        log.status,
      ]),
    );
    for (const [key, { status }] of Object.entries(pendingLogs)) {
      if (status === 'unlogged') map.delete(key);
      else map.set(key, status);
    }
    return map;
  });

  const formatDateNumber = (date: string) => {
    const value = new Date(`${date}T12:00:00`);
    return `${value.getMonth() + 1}/${value.getDate()}`;
  };
  const formatWeekday = (date: string) =>
    new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(new Date(`${date}T12:00:00`));
  const formatRange = (start: string, end: string) =>
    `${new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(`${start}T12:00:00`))} – ${new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(`${end}T12:00:00`))}`;
  const MOBILE_VISIBLE_DAYS = 5;
  const TABLET_VISIBLE_DAYS = 9;
  const visibleDateCount = () => {
    if ($viewport.isMobile) return MOBILE_VISIBLE_DAYS;
    if ($viewport.isTablet) return TABLET_VISIBLE_DAYS;
    return 14;
  };
  const compactTodayStart = () =>
    shiftDate(
      today,
      $viewport.isMobile ? -(MOBILE_VISIBLE_DAYS - 1) : -Math.floor(TABLET_VISIBLE_DAYS / 2),
    );
  let visibleDates = $derived.by(() => {
    const dateCount = visibleDateCount();
    if (dateCount === 14) return range.dates;
    // The server's default range centers Today at index 6. On phones, end the
    // initial window on Today; on tablets, center it in the nine-day window.
    const start = range.startDate === shiftDate(today, -6) ? compactTodayStart() : range.startDate;
    const startIndex = Math.max(range.dates.indexOf(start), 0);
    return range.dates.slice(startIndex, startIndex + dateCount);
  });
  let visibleRangeStart = $derived(visibleDates[0] || range.startDate);
  let visibleRangeEnd = $derived(visibleDates.at(-1) || range.endDate);
  const dateRangeStep = () => visibleDateCount();
  const centeredTodayStart = () =>
    visibleDateCount() === 14 ? shiftDate(today, -6) : compactTodayStart();
  const categoryFor = (id: string | null) => categories.find((category) => category.id === id);
  const statusFor = (habit: Habit, date: string) => logMap.get(`${habit.id}:${date}`);
  const eligible = isScheduledOn;

  async function request(path: string, options: RequestInit = {}) {
    const seq = navigationSeq;
    // Once Today mode holds the history, the answer refreshes it too.
    const query = `start_date=${range.startDate}${historyLoaded ? '&history=1' : ''}`;
    const response = await fetch(`${path}${path.includes('?') ? '&' : '?'}${query}`, {
      ...options,
      headers: jsonHeaders(),
    });
    if (!response.ok)
      throw new Error(await readApiErrorMessage(response, 'Could not save habit changes.'));
    const payload = (await response.json()) as HabitsPayload;
    // A navigation that started after this request owns the loaded days: keep
    // its window, and take only the rest.
    applyPayload(payload, { keepWindow: seq !== navigationSeq });
    return payload;
  }
  function colorLabel(color: string): string {
    return CATEGORY_COLOR_LABELS[color.toLowerCase()] || color.toUpperCase();
  }
  function tagStyleForColor(color: string): string {
    return `--cat-text:${color};--cat-bg:color-mix(in srgb, ${color} 12%, white);--cat-border:color-mix(in srgb, ${color} 28%, white);`;
  }
  function selectedHabitCategory(): HabitCategory | undefined {
    return categories.find((category) => category.id === habitForm.categoryId);
  }
  function selectedCategoryTagStyle(): string {
    return tagStyleForColor(selectedHabitCategory()?.color || '#94a3b8');
  }
  function selectedCategoryLabel(): string {
    return selectedHabitCategory()?.name || 'Uncategorized';
  }
  function swatchStyle(color: string): string {
    return `background:${color};`;
  }
  function showColorTooltip(text: string, anchor: HTMLElement) {
    if (colorTooltipTimer) clearTimeout(colorTooltipTimer);
    colorTooltipTimer = setTimeout(() => {
      const rect = anchor.getBoundingClientRect();
      colorTooltipText = text;
      colorTooltipPosition = { x: rect.left + rect.width / 2, y: rect.top - 4 };
    }, 400);
  }
  function hideColorTooltip() {
    if (colorTooltipTimer) clearTimeout(colorTooltipTimer);
    colorTooltipTimer = null;
    colorTooltipText = null;
    colorTooltipPosition = null;
  }
  async function positionAnchoredMenu(
    anchor: HTMLElement,
    menu: HTMLElement,
    setStyle: (style: string) => void,
  ) {
    const result = await computePosition(anchor, menu, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [
        offset(4),
        flip({ fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }),
        shift({ padding: 8 }),
      ],
    });
    setStyle(`position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`);
  }
  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate lifecycle: subscribes while the category picker is open.
  $effect(() => {
    if (!categoryMenuOpen || !categoryMenuAnchor || !categoryMenuEl) return;
    return autoUpdate(categoryMenuAnchor, categoryMenuEl, () =>
      positionAnchoredMenu(
        categoryMenuAnchor!,
        categoryMenuEl!,
        (style) => (categoryMenuStyle = style),
      ),
    );
  });
  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate lifecycle: subscribes while the manage color picker is open.
  $effect(() => {
    if (manageColorPopoverId === null || !manageColorAnchor || !manageColorEl) return;
    return autoUpdate(manageColorAnchor, manageColorEl, () =>
      positionAnchoredMenu(
        manageColorAnchor!,
        manageColorEl!,
        (style) => (manageColorStyle = style),
      ),
    );
  });
  // eslint-disable-next-line momentium/no-unguarded-effect -- document-level dismissal while the color popover is open; the modal card stops click propagation so only a document listener catches outside clicks.
  $effect(() => {
    if (manageColorPopoverId === null) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (manageColorAnchor?.contains(target) || manageColorEl?.contains(target)) return;
      closeManageColorPopover();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  });
  function applyPayload(payload: HabitsPayload, { keepWindow = false } = {}) {
    if (!keepWindow) {
      range = payload.range;
      logs = payload.logs;
      if (payload.history) {
        history = { rangeStart: payload.range.startDate, ...payload.history };
      }
    }
    habits = payload.habits;
    categories = payload.categories;
  }
  // Latest navigation wins: an answer that is no longer the newest is dropped,
  // so responses arriving out of order can't leave the wrong days loaded.
  let navigationSeq = 0;
  async function navigate(startDate: string): Promise<'ok' | 'stale' | 'error'> {
    const seq = ++navigationSeq;
    try {
      // Today mode also wants the history, so a streak isn't cut off at the range.
      const response = await fetch(
        `/habits?start_date=${startDate}${todayMode ? '&history=1' : ''}`,
        {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        },
      );
      if (!response.ok) throw new Error('Could not load dates.');
      const payload = (await response.json()) as HabitsPayload;
      if (seq !== navigationSeq) return 'stale';
      applyPayload(payload);
      return 'ok';
    } catch (error) {
      if (seq !== navigationSeq) return 'stale';
      toast.error(error instanceof Error ? error.message : 'Could not load dates.');
      return 'error';
    }
  }

  async function setStatus(habit: Habit, date: string, status: HabitLogStatus | 'unlogged') {
    if (!eligible(habit, date)) return;
    const key = `${habit.id}:${date}`;
    const id = ++pendingLogId;
    pendingLogs[key] = { status, id };
    try {
      await request(`/habits/items/${habit.id}/log`, {
        method: 'PATCH',
        body: JSON.stringify({ habit_log: { local_date: date, status } }),
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update habit.');
    } finally {
      if (pendingLogs[key]?.id === id) delete pendingLogs[key];
    }
  }
  const longPress = createLongPressTracker();
  const cellKey = (habit: Habit, date: string) => `${habit.id}:${date}`;
  const toggleMissed = (habit: Habit, date: string) =>
    setStatus(habit, date, statusFor(habit, date) === 'missed' ? 'unlogged' : 'missed');
  function cellPointerDown(event: PointerEvent, habit: Habit, date: string) {
    longPress.pointerDown(event, cellKey(habit, date), () => toggleMissed(habit, date));
  }
  function cellContextMenu(event: MouseEvent, habit: Habit, date: string) {
    longPress.contextMenu(event, cellKey(habit, date), () => toggleMissed(habit, date));
  }
  onDestroy(longPress.destroy);

  function cellAction(habit: Habit, date: string) {
    if (longPress.consumeClick(cellKey(habit, date))) return;
    const status = statusFor(habit, date);
    setStatus(habit, date, status === 'completed' ? 'unlogged' : 'completed');
  }
  function cellKeydown(event: KeyboardEvent, habit: Habit, date: string) {
    if (event.key.toLowerCase() !== 'm') return;
    event.preventDefault();
    toggleMissed(habit, date);
  }
  function openHabit(habit: Habit | null = null) {
    editingHabit = habit;
    habitForm = habit
      ? {
          title: habit.title,
          description: habit.description || '',
          categoryId: habit.categoryId || '',
          startDate: habit.startDate,
        }
      : { title: '', description: '', categoryId: '', startDate: today };
    habitFormBaseline = JSON.stringify(habitForm);
    closeCategoryMenu();
    habitModal = true;
  }
  function closeHabitModal() {
    if (categoryMenuOpen) closeCategoryMenu();
    if (habitFormDirty) {
      discardConfirmation = 'habit';
      return;
    }
    habitModalAnimation.animateClose();
  }
  function discardChanges() {
    const source = discardConfirmation;
    discardConfirmation = null;
    if (source === 'categories') {
      closeManageColorPopover();
      manageCategoriesModal.animateClose();
      return;
    }
    closeCategoryMenu();
    habitModalAnimation.animateClose();
  }
  function openCategoryMenu(anchor: HTMLElement) {
    categoryMenuStyle = 'position: fixed; left: 0; top: 0; visibility: hidden;';
    categoryNewMode = false;
    categoryNewName = '';
    categoryNewColor = DEFAULT_CATEGORY_COLOR;
    categoryMenuOpen = true;
    categoryMenuAnchor = anchor;
  }
  function closeCategoryMenu() {
    categoryMenuOpen = false;
    categoryMenuAnchor = undefined;
    categoryNewMode = false;
  }
  function pickCategory(categoryId: string) {
    habitForm.categoryId = categoryId;
    closeCategoryMenu();
  }
  async function createCategoryFromPicker() {
    if (categorySaving || !categoryNewName.trim()) return;
    categorySaving = true;
    try {
      const payload = await request('/habits/categories', {
        method: 'POST',
        body: JSON.stringify({
          habit_category: {
            name: categoryNewName.trim(),
            color: categoryNewColor,
            tagline: '',
            completion_threshold_percent: 100,
          },
        }),
      });
      const createdId =
        payload.createdCategoryId ??
        payload.categories.find((category) => category.name === categoryNewName.trim())?.id;
      if (createdId) habitForm.categoryId = createdId;
      closeCategoryMenu();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not add category.');
    } finally {
      categorySaving = false;
    }
  }
  async function saveHabit() {
    if (!habitForm.title.trim()) return;
    saving = true;
    try {
      const body = JSON.stringify({
        habit: {
          title: habitForm.title,
          description: habitForm.description,
          habit_category_id: habitForm.categoryId || null,
          start_date: habitForm.startDate,
        },
      });
      await request(editingHabit ? `/habits/items/${editingHabit.id}` : '/habits/items', {
        method: editingHabit ? 'PATCH' : 'POST',
        body,
      });
      closeCategoryMenu();
      habitModal = false;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save habit.');
    } finally {
      saving = false;
    }
  }
  async function archive(habit: Habit) {
    if (!habit.archivedAt) {
      archiveConfirmation = habit;
      return;
    }
    try {
      await request(`/habits/items/${habit.id}/restore`, { method: 'POST' });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update habit.');
    }
  }
  async function confirmArchive() {
    const habit = archiveConfirmation;
    if (!habit) return;
    try {
      await request(`/habits/items/${habit.id}/archive`, { method: 'POST' });
      archiveConfirmation = null;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not archive habit.');
    }
  }
  function manageEditingHabit(action: 'archive' | 'delete') {
    const habit = editingHabit;
    if (!habit) return;
    closeCategoryMenu();
    habitModal = false;
    if (action === 'archive') {
      void archive(habit);
    } else {
      removeHabit(habit);
    }
  }
  function removeHabit(habit: Habit) {
    deleteConfirmation = habit;
  }
  async function confirmDeleteHabit() {
    const habit = deleteConfirmation;
    if (!habit) return;
    try {
      await request(`/habits/items/${habit.id}`, { method: 'DELETE' });
      deleteConfirmation = null;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete habit.');
    }
  }
  function closeSettingsMenu() {
    settingsMenuPinned = false;
    settingsMenuHovered = false;
  }
  function openManageCategories() {
    closeSettingsMenu();
    closeCategoryMenu();
    closeManageColorPopover();
    manageCategoryDrafts = categories.map((category) => ({ ...category }));
    resetNewManageCategory();
    manageCategoriesOpen = true;
  }
  function openViewSettings() {
    closeSettingsMenu();
    viewSettingsOpen = true;
  }
  function closeViewSettings() {
    viewSettingsModal.animateClose();
  }
  function closeManageCategories() {
    closeManageColorPopover();
    if (manageCategoriesDirty) {
      discardConfirmation = 'categories';
      return;
    }
    manageCategoriesModal.animateClose();
  }
  function syncManageDrafts() {
    manageCategoryDrafts = categories.map((category) => ({ ...category }));
  }
  function resetNewManageCategory() {
    newManageCategoryName = '';
    newManageCategoryColor = null;
    newManageCategoryTagline = '';
    newManageCategoryThreshold = 100;
  }
  let manageCategoriesDirty = $derived(
    manageCategoriesOpen &&
      (manageCategoryDrafts.some((draft) => !manageDraftUnchanged(draft)) ||
        categoryOrderChanged() ||
        newManageCategoryName.trim() !== '' ||
        newManageCategoryColor !== null ||
        newManageCategoryTagline.trim() !== '' ||
        newManageCategoryThreshold !== 100),
  );
  let hasPersistableManageChanges = $derived(
    manageCategoryDrafts.some((draft) => draft.name.trim() && !manageDraftUnchanged(draft)) ||
      categoryOrderChanged() ||
      newManageCategoryName.trim() !== '',
  );
  function categoryPayload(draft: {
    name: string;
    color: string;
    tagline: string | null;
    completionThresholdPercent: number;
  }) {
    return {
      habit_category: {
        name: draft.name.trim(),
        color: draft.color,
        tagline: draft.tagline || '',
        completion_threshold_percent: draft.completionThresholdPercent,
      },
    };
  }
  function categoryOrderChanged() {
    return manageCategoryDrafts.some((draft, index) => draft.id !== categories[index]?.id);
  }
  function moveDraftCategoryBy(index: number, direction: MoveDirection) {
    manageCategoryDrafts = moveItem(manageCategoryDrafts, index, direction) ?? manageCategoryDrafts;
  }
  function moveDraftCategory(targetId: string) {
    if (!draggedCategoryId || draggedCategoryId === targetId) return;
    const from = manageCategoryDrafts.findIndex((category) => category.id === draggedCategoryId);
    const to = manageCategoryDrafts.findIndex((category) => category.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...manageCategoryDrafts];
    next.splice(to, 0, next.splice(from, 1)[0]);
    manageCategoryDrafts = next;
  }
  function manageDraftUnchanged(draft: HabitCategory) {
    const original = categories.find((category) => category.id === draft.id);
    return (
      !!original &&
      original.name === draft.name.trim() &&
      original.color === draft.color &&
      (original.tagline || '') === (draft.tagline || '') &&
      original.completionThresholdPercent === draft.completionThresholdPercent
    );
  }
  async function saveManageCategories() {
    if (
      manageCategorySaving ||
      !hasPersistableManageChanges ||
      manageCategoryDrafts.some((draft) => !draft.name.trim())
    )
      return;
    manageCategorySaving = true;
    try {
      for (const draft of manageCategoryDrafts) {
        if (manageDraftUnchanged(draft)) continue;
        // Sequential writes so a rejected row keeps later drafts unsaved.
        // eslint-disable-next-line no-await-in-loop -- persist categories one at a time
        await request(`/habits/categories/${draft.id}`, {
          method: 'PATCH',
          body: JSON.stringify(categoryPayload(draft)),
        });
      }
      if (newManageCategoryName.trim()) {
        await request('/habits/categories', {
          method: 'POST',
          body: JSON.stringify(
            categoryPayload({
              name: newManageCategoryName,
              color: newManageCategoryColor ?? DEFAULT_CATEGORY_COLOR,
              tagline: newManageCategoryTagline,
              completionThresholdPercent: newManageCategoryThreshold,
            }),
          ),
        });
        resetNewManageCategory();
      }
      const orderedIds = [
        ...manageCategoryDrafts.map((category) => category.id),
        ...categories
          .filter((category) => !manageCategoryDrafts.some((draft) => draft.id === category.id))
          .map((category) => category.id),
      ];
      await request('/habits/categories_order', {
        method: 'PATCH',
        body: JSON.stringify({ habit_categories: { ids: orderedIds } }),
      });
      syncManageDrafts();
      closeManageColorPopover();
      manageCategoriesModal.animateClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save categories.');
    } finally {
      manageCategorySaving = false;
    }
  }
  function openManageColorPopover(id: string, anchor: HTMLElement) {
    manageColorStyle = 'position: fixed; left: 0; top: 0; visibility: hidden;';
    manageColorPopoverId = id;
    manageColorAnchor = anchor;
  }
  function closeManageColorPopover() {
    hideColorTooltip();
    manageColorPopoverId = null;
    manageColorAnchor = undefined;
  }
  function pickManageColor(color: string) {
    const id = manageColorPopoverId;
    if (id === null) return;
    if (id === 'new') {
      newManageCategoryColor = color;
    } else {
      const draft = manageCategoryDrafts.find((entry) => entry.id === id);
      if (draft) draft.color = color;
    }
    closeManageColorPopover();
  }
  function requestRemoveManageCategory(draft: HabitCategory) {
    categoryDeleteConfirmation = draft;
  }
  async function confirmDeleteCategory() {
    const category = categoryDeleteConfirmation;
    if (!category || manageCategorySaving) return;
    manageCategorySaving = true;
    try {
      await request(`/habits/categories/${category.id}`, { method: 'DELETE' });
      categoryDeleteConfirmation = null;
      manageCategoryDrafts = manageCategoryDrafts.filter((draft) => draft.id !== category.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete category.');
    } finally {
      manageCategorySaving = false;
    }
  }
  /** The habits a habit can be reordered among: its category's active habits, in board order. */
  function reorderGroup(habit: Habit) {
    return sortGroupHabits(
      habits.filter((other) => !other.archivedAt && other.categoryId === habit.categoryId),
    );
  }
  async function reorderHabits(targetId: string) {
    if (!draggedHabitId || draggedHabitId === targetId) return;
    const dragged = habits.find((habit) => habit.id === draggedHabitId);
    const target = habits.find((habit) => habit.id === targetId);
    if (
      !dragged ||
      !target ||
      dragged.archivedAt ||
      target.archivedAt ||
      dragged.categoryId !== target.categoryId
    )
      return;

    await moveHabitTo(
      dragged,
      reorderGroup(dragged).findIndex((habit) => habit.id === target.id),
    );
  }
  /** Drop and Move up / Move down: moves `moved` to index `to` of its reorder group. */
  async function moveHabitTo(moved: Habit, to: number) {
    const group = reorderGroup(moved);
    const from = group.findIndex((habit) => habit.id === moved.id);
    if (from < 0 || to < 0 || to >= group.length) return;
    const reordered = [...group];
    reordered.splice(to, 0, reordered.splice(from, 1)[0]);
    const originalHabits = habits;
    const byId = new Map(reordered.map((habit, position) => [habit.id, { ...habit, position }]));
    habits = habits.map((habit) => byId.get(habit.id) ?? habit);
    draggedHabitId = null;
    try {
      await request('/habits/items_order', {
        method: 'PATCH',
        body: JSON.stringify({ habits: { ids: reordered.map((habit) => habit.id) } }),
      });
    } catch (error) {
      habits = originalHabits;
      toast.error(error instanceof Error ? error.message : 'Could not reorder habits.');
    }
  }

  // Touch: ⋯ action sheets stand in for the drag handles (plans/mobile-v2.md §3.5).
  let rowSheet = $state<{ title: string; actions: SheetAction[] } | null>(null);
  let rowSheetOpen = $state(false);
  function openRowSheet(title: string, actions: SheetAction[]) {
    rowSheet = { title, actions };
    rowSheetOpen = true;
  }
  // Phone header ⚙: the desktop Settings menu plus Show archived.
  function openSettingsSheet() {
    openRowSheet('Habits settings', [
      { label: 'Manage categories', onSelect: openManageCategories },
      { label: 'View settings', onSelect: openViewSettings },
      {
        label: showArchived ? 'Hide archived habits' : 'Show archived habits',
        onSelect: () => (showArchived = !showArchived),
      },
    ]);
  }
  function openHabitSheet(habit: Habit) {
    const group = reorderGroup(habit);
    const index = group.findIndex((other) => other.id === habit.id);
    openRowSheet(habit.title, [
      // Today mode: the tap alternative to a long press.
      ...(todayMode && eligible(habit, checkInDate)
        ? [
            {
              label: statusFor(habit, checkInDate) === 'missed' ? 'Clear missed' : 'Mark as missed',
              icon: 'close' as const,
              onSelect: () => toggleMissed(habit, checkInDate),
            },
          ]
        : []),
      ...moveUpDownActions(index, group.length, (direction) =>
        moveHabitTo(habit, index + direction),
      ),
      { label: 'Edit habit…', icon: 'edit', onSelect: () => openHabit(habit) },
    ]);
  }
  function openDraftCategorySheet(index: number) {
    const category = manageCategoryDrafts[index];
    openRowSheet(
      category.name.trim() || 'Category',
      moveUpDownActions(index, manageCategoryDrafts.length, (direction) =>
        moveDraftCategoryBy(index, direction),
      ),
    );
  }

  function handlePageKeydown(event: KeyboardEvent) {
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      event.target instanceof HTMLSelectElement ||
      (event.target instanceof HTMLElement && event.target.isContentEditable) ||
      habitModal ||
      manageCategoriesOpen ||
      viewSettingsOpen ||
      isDialogOpen()
    )
      return;
    if (shouldHandleTodayHotkey(event, { dialogOpen: false })) {
      event.preventDefault();
      if (todayMode) goToDay(today);
      else navigate(centeredTodayStart());
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      if (todayMode) goToDay(shiftDate(checkInDate, -1));
      else navigate(shiftDate(visibleRangeStart, -dateRangeStep()));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      if (!todayMode) navigate(shiftDate(visibleRangeStart, dateRangeStep()));
      else if (checkInDate < today) goToDay(shiftDate(checkInDate, 1));
    }
  }
  function handleModalKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    if (categoryMenuOpen) {
      event.preventDefault();
      closeCategoryMenu();
      return;
    }
    if (manageColorPopoverId !== null) {
      event.preventDefault();
      closeManageColorPopover();
      return;
    }
    if (categoryDeleteConfirmation) {
      event.preventDefault();
      categoryDeleteModal.animateClose();
      return;
    }
    if (discardConfirmation) {
      event.preventDefault();
      discardModal.animateClose();
      return;
    }
    if (settingsMenuOpen) {
      event.preventDefault();
      closeSettingsMenu();
      settingsTriggerEl?.focus();
      return;
    }
    if (habitModal) closeHabitModal();
    if (manageCategoriesOpen) closeManageCategories();
    if (viewSettingsOpen) closeViewSettings();
  }
  function handleMenuOutsideClick(event: MouseEvent) {
    const target = event.target as Node;
    if (settingsMenuOpen && !(target as Element).closest('.settings-menu-wrap')) {
      closeSettingsMenu();
    }
    if (
      categoryMenuOpen &&
      !categoryMenuAnchor?.contains(target) &&
      !categoryMenuEl?.contains(target)
    ) {
      closeCategoryMenu();
    }
  }
  function progress(categoryId: string | null, date: string) {
    const items = visibleHabits.filter(
      (habit) => habit.categoryId === categoryId && eligible(habit, date),
    );
    const completed = items.filter((habit) => statusFor(habit, date) === 'completed').length;
    const category = categoryFor(categoryId);
    return {
      completed,
      total: items.length,
      onTrack:
        !!category &&
        items.length > 0 &&
        (completed / items.length) * 100 >= category.completionThresholdPercent,
    };
  }

  // === Phone "Today" check-in mode (plans/mobile-v2.md ma-twon) ===
  // Only phones get it; tablets and desktop keep the grid. The stored choice is
  // per device, and a phone opens in Today until it picks otherwise.
  let storedMode = $state<HabitsMode | null>(readHabitsMode());
  let todayMode = $derived($viewport.isMobile && (storedMode ?? DEFAULT_HABITS_MODE) === 'today');
  let selectedDay = $state<string | null>(null);
  let checkInDate = $derived(selectedDay ?? today);
  // The first date whose logs we hold: the history's start once it has loaded,
  // else the range's own.
  let oldestLoadedLog = $derived(historyLoaded ? history!.startDate : range.startDate);
  // The day's sparkline (its last 7 days) must be inside the logs we hold, or
  // its targets would read blank over logs that exist: show nothing to tap
  // until they arrive. `dayOutsideRange` asks for a window around the day even
  // when the logs would do, so its streaks get their full history.
  let dayLogsLoaded = $derived(
    shiftDate(checkInDate, -(SPARKLINE_DAYS - 1)) >= oldestLoadedLog &&
      checkInDate <= range.endDate,
  );
  let dayOutsideRange = $derived(checkInDate < range.startDate || checkInDate > range.endDate);
  let dayLoadFailed = $state(false);
  async function loadDay(date: string) {
    dayLoadFailed = false;
    dayLoadFailed = (await navigate(shiftDate(date, -6))) === 'error';
  }
  // eslint-disable-next-line momentium/no-unguarded-effect -- fetches a window around the day when Today mode shows one outside the loaded range (mode switch, day change, resize, ?start_date=). The fetch is the only write and it ends the condition; a failure changes no dependency, so it doesn't retry in a loop.
  $effect(() => {
    if (!todayMode || !dayOutsideRange) return;
    const date = checkInDate;
    untrack(() => void loadDay(date));
  });
  // The range start whose history failed to load; it isn't asked for again.
  let historyFailedFor = $state<string | null>(null);
  // The range start whose history request is in flight. Not reactive: the effect
  // below must not re-run (and ask again) when it changes.
  let historyInFlight: string | null = null;
  async function loadHistory(rangeStart: string) {
    // The effect re-runs when the mode or the day toggles mid-request; one is enough.
    if (historyInFlight === rangeStart) return;
    historyInFlight = rangeStart;
    try {
      const response = await fetch(`/habits?start_date=${rangeStart}&history=1`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error('Could not load streaks.');
      const payload = (await response.json()) as HabitsPayload;
      // Only the history: `logs` may already hold newer taps than this snapshot.
      // Drop it if the window moved on meanwhile, or if the history for this
      // window is already here (a save's answer refreshes it, and is newer).
      if (range.startDate !== rangeStart || !payload.history) return;
      if (history?.rangeStart === rangeStart) return;
      history = { rangeStart, ...payload.history };
    } catch {
      // Streaks fall back to the loaded range ("7+"), but a past day whose
      // sparkline lies before the range needs the window fetch (see below).
      historyFailedFor = rangeStart;
      dayLoadFailed = true;
    } finally {
      if (historyInFlight === rangeStart) historyInFlight = null;
    }
  }
  // eslint-disable-next-line momentium/no-unguarded-effect -- fetches the logs before the range once when Today mode opens (or its window moves). The fetch is the only write and it ends the condition; a failure records the range start in historyFailedFor, so it can't retry in a loop.
  $effect(() => {
    // A day outside the range loads its window instead, history included.
    if (!todayMode || dayOutsideRange || historyLoaded) return;
    const rangeStart = range.startDate;
    if (historyFailedFor === rangeStart) return;
    untrack(() => void loadHistory(rangeStart));
  });
  let todayGroups = $derived.by<HabitsTodayGroup[]>(() =>
    grouped
      .map(([categoryId, groupHabits]) => {
        const category = categoryFor(categoryId === 'uncategorized' ? null : categoryId) ?? null;
        const { completed, total } = progress(category?.id ?? null, checkInDate);
        return {
          id: categoryId,
          category,
          habits: groupHabits.filter((habit) => eligible(habit, checkInDate)),
          completed,
          total,
        };
      })
      .filter((group) => group.habits.length > 0),
  );

  function setMode(mode: HabitsMode) {
    storedMode = mode;
    saveHabitsMode(mode);
    // Today mode may have moved the loaded range; the grid opens on today.
    if (mode === 'grid' && visibleRangeEnd !== today) void navigate(centeredTodayStart());
  }
  function goToDay(date: string) {
    selectedDay = date >= today ? null : date;
  }
  const toggleDone = (habit: Habit) =>
    setStatus(
      habit,
      checkInDate,
      statusFor(habit, checkInDate) === 'completed' ? 'unlogged' : 'completed',
    );
</script>

<svelte:head><title>Habits · Momentium</title></svelte:head>
<svelte:window
  onkeydown={(event) => {
    handlePageKeydown(event);
    handleModalKeydown(event);
  }}
  onmousedown={handleMenuOutsideClick}
  onresize={() => {
    if (infoHabitId) measureInfoPlacement();
  }}
/>
{#if $viewport.isMobile}
  <MobileTopBar title="Habits">
    {#snippet actions()}
      <button type="button" class="mtb-icon-btn" aria-label="Add habit" onclick={() => openHabit()}>
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
          ><path d="M12 5v14M5 12h14" /></svg
        >
      </button>
      <button
        type="button"
        class="mtb-icon-btn"
        aria-label="Open Habits settings"
        aria-haspopup="dialog"
        onclick={openSettingsSheet}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path
            d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 1 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"
          />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
    {/snippet}
    {#snippet below()}
      <SegmentedControl
        label="Habits view"
        value={todayMode ? 'today' : 'grid'}
        options={[
          { value: 'today', label: 'Today' },
          { value: 'grid', label: 'Week grid' },
        ]}
        onChange={setMode}
      />
    {/snippet}
  </MobileTopBar>
{:else}
  <header class="page-header">
    <div class="header-left">
      <Link href="/" class="logo">
        <img src={momentiumLogo} alt="Momentium" class="logo-icon" />
        <span class="logo-text">Momentium</span>
      </Link>
      <NavDropdown activePage="habits" />
    </div>
    <div class="header-right"><ProfileDropdown /></div>
  </header>
{/if}
<div class="page">
  <main class="page-body">
    {#if !$viewport.isMobile}<section class="page-intro">
        <div>
          <h1>Habits</h1>
          <p class="hero-copy">
            Small daily actions, seen over time. Blank days stay yours to fill in whenever you’re
            ready.
          </p>
        </div>
        <div class="actions">
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div
            class="settings-menu-wrap"
            onmouseenter={() => (settingsMenuHovered = true)}
            onmouseleave={() => (settingsMenuHovered = false)}
            onfocusout={(event) => {
              if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node)) {
                closeSettingsMenu();
              }
            }}
          >
            <button
              type="button"
              class="quiet settings-trigger"
              bind:this={settingsTriggerEl}
              aria-label="Open Habits settings"
              aria-haspopup="menu"
              aria-expanded={settingsMenuOpen}
              onclick={(event) => {
                event.stopPropagation();
                settingsMenuPinned = !settingsMenuPinned;
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 1 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
                <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" />
              </svg>
              <span>Settings</span>
              <svg
                class="settings-chevron"
                viewBox="0 0 10 6"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.2" fill="none" />
              </svg>
            </button>
            <!-- svelte-ignore a11y_click_events_have_key_events a11y_interactive_supports_focus -->
            <div
              class="settings-menu"
              class:open={settingsMenuOpen}
              role="menu"
              tabindex="-1"
              onclick={(event) => event.stopPropagation()}
            >
              <button type="button" role="menuitem" onclick={openManageCategories}
                >Manage categories</button
              >
              <button type="button" role="menuitem" onclick={openViewSettings}>View settings</button
              >
            </div>
          </div>
          <button class="primary" onclick={() => openHabit()}>+ Add habit</button>
        </div>
      </section>{/if}
    <section class="toolbar">
      {#if todayMode}
        <DateRangeNav
          start={checkInDate}
          end={checkInDate}
          {today}
          context={{ text: relativeDayLabel(daysBetween(checkInDate, today)), tone: 'prior' }}
          previousLabel="Previous day"
          nextLabel="Next day"
          nextDisabled={checkInDate >= today}
          onPrevious={() => goToDay(shiftDate(checkInDate, -1))}
          onNext={() => goToDay(shiftDate(checkInDate, 1))}
          onToday={() => goToDay(today)}
        />
      {:else}
        <DateRangeNav
          --drn-gap="10px"
          --drn-label-min-width="255px"
          --drn-today-gap="8px"
          start={visibleRangeStart}
          end={visibleRangeEnd}
          {today}
          label={formatRange(visibleRangeStart, visibleRangeEnd)}
          context={visibleRangeEnd < today
            ? { text: `${daysBetween(visibleRangeEnd, today)} days prior`, tone: 'prior' }
            : visibleRangeStart > today
              ? { text: `${daysBetween(today, visibleRangeStart)} days in future`, tone: 'future' }
              : undefined}
          previousLabel="Show earlier days"
          nextLabel="Show later days"
          onPrevious={() => navigate(shiftDate(visibleRangeStart, -dateRangeStep()))}
          onNext={() => navigate(shiftDate(visibleRangeStart, dateRangeStep()))}
          onToday={() => navigate(centeredTodayStart())}
        >
          {#snippet trailing()}
            {#if !$viewport.isMobile}
              <label class="archived"
                ><input type="checkbox" bind:checked={showArchived} /> Show archived</label
              >
            {/if}
          {/snippet}
        </DateRangeNav>
      {/if}
    </section>
    {#if todayMode}
      {#if !dayLogsLoaded}
        <div class="empty" aria-busy={!dayLoadFailed}>
          {#if dayLoadFailed}
            <h2>Couldn’t load this day</h2>
            <button class="primary" onclick={() => loadDay(checkInDate)}>Try again</button>
          {:else}
            <p>Loading…</p>
          {/if}
        </div>
      {:else if todayGroups.length > 0}
        <HabitsToday
          groups={todayGroups}
          date={checkInDate}
          {statusFor}
          oldestLoaded={oldestLoadedLog}
          useCategoryColor={$habitsViewSettings.useCategoryColor}
          fallbackColor={DEFAULT_COMPLETED_CELL_COLOR}
          onToggleDone={toggleDone}
          onToggleMissed={(habit) => toggleMissed(habit, checkInDate)}
          onMore={openHabitSheet}
        />
      {:else if habits.length === 0}
        <div class="empty">
          <h2>Start your board</h2>
          <p>Add a habit to begin a calm, editable record of your daily progress.</p>
          <button class="primary" onclick={() => openHabit()}>Add your first habit</button>
        </div>
      {:else}
        <div class="empty">
          <h2>Nothing scheduled</h2>
          <p>No habits were active on this day.</p>
        </div>
      {/if}
    {:else}
      <div class="board-wrap">
        <div class="sticky-head">
          <div
            class="head grid"
            style={`--date-columns: ${visibleDates.length}; transform: translateX(-${$viewport.isDesktop ? boardScrollLeft : 0}px)`}
          >
            <div class="habit-head">Habit</div>
            {#each visibleDates as date}<div class:today={date === today} class="date">
                <span>{formatDateNumber(date)}</span>
                <small>{formatWeekday(date)}</small>
              </div>{/each}
          </div>
        </div>
        <div
          class="board-scroll"
          onscroll={(event) => {
            boardScrollLeft = event.currentTarget.scrollLeft;
            if (infoHabitId) measureInfoPlacement();
          }}
        >
          <div class="board" style={`--date-columns: ${visibleDates.length}`}>
            {#if grouped.length === 0}<div class="empty">
                <h2>Start your board</h2>
                <p>Add a habit to begin a calm, editable record of your daily progress.</p>
                <button class="primary" onclick={() => openHabit()}>Add your first habit</button>
              </div>{/if}
            {#each grouped as [categoryId, groupHabits]}
              {@const category = categoryFor(categoryId === 'uncategorized' ? null : categoryId)}
              <section class="group">
                <div
                  class="group-title"
                  class:compact={$habitsViewSettings.categoryHeaderStyle === 'compact'}
                  style={`--group-color: ${category?.color || '#94a3b8'}`}
                >
                  <div>
                    <h2>
                      <span class="group-color-dot" aria-hidden="true"></span>{category?.name ||
                        'Uncategorized'}{#if $habitsViewSettings.categoryHeaderStyle === 'compact' && category?.tagline}<span
                          class="group-tagline-inline">· {category.tagline}</span
                        >{/if}
                    </h2>
                    {#if $habitsViewSettings.categoryHeaderStyle === 'normal' && category?.tagline}<p
                      >
                        {category.tagline}
                      </p>{/if}
                  </div>
                  {#if category}<button
                      class="icon"
                      aria-label={`Edit ${category.name}`}
                      onclick={openManageCategories}>⚙</button
                    >{/if}
                </div>
                <div class="group-progress grid">
                  <div>
                    {#if category}{@const p = progress(category.id, today)}<span
                        class:on-track={p.onTrack}
                        >{p.completed} of {p.total} checked in{#if p.onTrack}
                          · On track{/if}</span
                      >{/if}
                  </div>
                  {#each visibleDates as date}{@const p = progress(category?.id || null, date)}
                    <div class:on-track={p.onTrack} class:today={date === today}>
                      {p.total ? `${p.completed}/${p.total}` : ''}
                    </div>{/each}
                </div>
                {#each groupHabits as habit}<div
                    class="habit-row grid"
                    class:dragging={draggedHabitId === habit.id}
                  >
                    <div
                      class="habit-name"
                      role="group"
                      aria-label={habit.title}
                      ondragover={(event) => {
                        if (!habit.archivedAt) event.preventDefault();
                      }}
                      ondrop={(event) => {
                        event.preventDefault();
                        void reorderHabits(habit.id);
                      }}
                    >
                      {#if !habit.archivedAt && $tapAlternatives}<button
                          type="button"
                          class="row-menu-btn"
                          aria-label={`Actions for ${habit.title}`}
                          onclick={() => openHabitSheet(habit)}
                          ><Icon name="more" size={18} /></button
                        >{:else if !habit.archivedAt}<button
                          type="button"
                          class="drag-handle"
                          draggable="true"
                          aria-label={`Drag to reorder ${habit.title}`}
                          title="Drag to reorder"
                          ondragstart={(event) => {
                            draggedHabitId = habit.id;
                            event.dataTransfer?.setData('text/plain', habit.id);
                            setCategoryDragImage(
                              event,
                              habit.title,
                              categoryFor(habit.categoryId)?.color || '#94a3b8',
                            );
                          }}
                          ondragend={() => (draggedHabitId = null)}>⠿</button
                        >{/if}
                      <div class="habit-details">
                        <button
                          class="habit-edit"
                          use:clampOverflowAction={habit.id}
                          aria-describedby={infoVisibleFor(habit)
                            ? `habit-info-${habit.id}`
                            : undefined}
                          onmouseenter={() => infoEnter(habit.id)}
                          onmouseleave={() => infoLeave(habit.id)}
                          onfocus={() => infoEnter(habit.id)}
                          onblur={() => infoLeave(habit.id)}
                          onclick={() => openHabit(habit)}>{habit.title}</button
                        >
                      </div>
                      {#if infoVisibleFor(habit)}
                        <div
                          class="habit-info"
                          class:above={infoPlacement === 'above'}
                          class:measuring={infoPending}
                          id={`habit-info-${habit.id}`}
                          role="tooltip"
                          bind:this={popoverEl}
                          style={infoMaxHeight !== undefined &&
                          infoMaxHeight < INFO_POPOVER_MAX_HEIGHT_PX
                            ? `max-height: ${infoMaxHeight}px`
                            : ''}
                          onmouseenter={() => infoPopoverEnter(habit.id)}
                          onmouseleave={() => infoPopoverLeave(habit.id)}
                        >
                          <div class="habit-info-title">{habit.title}</div>
                          {#if habit.description}<div class="habit-info-description">
                              {habit.description}
                            </div>{/if}
                        </div>
                      {/if}
                    </div>
                    {#each visibleDates as date}{@const state = statusFor(habit, date)}
                      {@const categoryColor =
                        categoryFor(habit.categoryId)?.color || DEFAULT_COMPLETED_CELL_COLOR}
                      {@const completedColor = $habitsViewSettings.useCategoryColor
                        ? categoryColor
                        : DEFAULT_COMPLETED_CELL_COLOR}
                      <div class:today={date === today} class="cell">
                        <button
                          class:completed={state === 'completed'}
                          class:missed={state === 'missed'}
                          class:disabled={!eligible(habit, date)}
                          style={`--cell-color: ${categoryColor}; --completed-color: ${completedColor}`}
                          aria-label={`${habit.title}, ${date}: ${state || 'unlogged'}`}
                          disabled={!eligible(habit, date)}
                          aria-keyshortcuts="M"
                          title="Tap to complete. Press and hold, press M, or right-click to mark missed."
                          onclick={() => cellAction(habit, date)}
                          onpointerdown={(event) => cellPointerDown(event, habit, date)}
                          onpointermove={longPress.pointerMove}
                          onpointerup={longPress.pointerEnd}
                          onpointercancel={longPress.pointerEnd}
                          onpointerleave={longPress.pointerEnd}
                          onkeydown={(event) => cellKeydown(event, habit, date)}
                          oncontextmenu={(event) => cellContextMenu(event, habit, date)}
                          >{#if $habitsViewSettings.showStatusIcons && state === 'completed'}
                            <svg
                              class="cell-icon cell-icon-check"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                              focusable="false"
                            >
                              <path d="m5 12.5 4.5 4.5L19 7.5" />
                            </svg>
                          {:else if $habitsViewSettings.showStatusIcons && state === 'missed'}
                            <svg
                              class="cell-icon cell-icon-missed"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                              focusable="false"
                            >
                              <path d="m7 7 10 10M17 7 7 17" />
                            </svg>
                          {/if}
                          <span class="cell-label"
                            >{state === 'completed'
                              ? 'Unmark'
                              : state === 'missed'
                                ? 'Missed'
                                : habit.title}</span
                          ></button
                        >
                      </div>{/each}
                  </div>{/each}
              </section>
            {/each}
          </div>
        </div>
      </div>
    {/if}
  </main>
</div>

<!-- svelte-ignore a11y_click_events_have_key_events a11y_interactive_supports_focus -->
{#if habitModal}<div
    class="modal-backdrop"
    class:closing={habitModalAnimation.closing}
    bind:this={habitModalAnimation.overlayEl}
    onclick={(event) => {
      if (event.target === event.currentTarget) closeHabitModal();
    }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="habit-modal-title"
    tabindex="-1"
  >
    <div class="modal form-modal">
      <div class="modal-header">
        <h2 id="habit-modal-title">{editingHabit ? 'Edit habit' : 'Add habit'}</h2>
        <button type="button" class="modal-close" onclick={closeHabitModal} aria-label="Close"
          >×</button
        >
      </div>
      <form
        class="modal-body"
        onsubmit={(event) => {
          event.preventDefault();
          saveHabit();
        }}
      >
        <label class="modal-field"
          >Habit name<input
            bind:value={habitForm.title}
            maxlength="250"
            autocomplete="off"
            placeholder="e.g. Meditate"
          /></label
        >
        <label class="modal-field"
          >Description <span class="field-hint">(optional)</span><textarea
            bind:value={habitForm.description}
            autocomplete="off"
            placeholder="Helpful context for future you…"></textarea></label
        >
        <div class="modal-field">
          <span>Category</span>
          <button
            type="button"
            class="category-picker-trigger"
            onclick={(event) =>
              categoryMenuOpen
                ? closeCategoryMenu()
                : openCategoryMenu(event.currentTarget as HTMLElement)}
            aria-haspopup="listbox"
            aria-expanded={categoryMenuOpen}
          >
            <span class="category-tag" style={selectedCategoryTagStyle()}
              >{selectedCategoryLabel()}</span
            >
            <span class="status-caret" aria-hidden="true">▾</span>
          </button>
        </div>
        <label class="modal-field"
          >Start date<input
            type="date"
            bind:value={habitForm.startDate}
            autocomplete="off"
          /></label
        >
        {#if editingHabit}
          <div class="habit-management-actions">
            <button type="button" class="quiet" onclick={() => manageEditingHabit('archive')}
              >{editingHabit.archivedAt ? 'Restore habit' : 'Archive habit'}</button
            >
            <button type="button" class="danger" onclick={() => manageEditingHabit('delete')}
              >Delete habit</button
            >
          </div>
        {/if}
        <div class="modal-actions">
          <button type="button" class="quiet" onclick={closeHabitModal}>Cancel</button><button
            class="primary"
            disabled={saving}>{editingHabit ? 'Save habit' : 'Add habit'}</button
          >
        </div>
      </form>
    </div>
  </div>{/if}
{#if categoryMenuOpen}
  <div
    class="category-menu-popover"
    bind:this={categoryMenuEl}
    style={categoryMenuStyle}
    role="listbox"
    aria-label="Choose a category"
  >
    {#if !categoryNewMode}
      <button
        type="button"
        class="category-option"
        class:active={!habitForm.categoryId}
        onclick={() => pickCategory('')}
        role="option"
        aria-selected={!habitForm.categoryId}
      >
        <span class="category-tag" style={tagStyleForColor('#94a3b8')}>Uncategorized</span>
      </button>
      {#each categories as category (category.id)}
        <button
          type="button"
          class="category-option"
          class:active={habitForm.categoryId === category.id}
          onclick={() => pickCategory(category.id)}
          role="option"
          aria-selected={habitForm.categoryId === category.id}
        >
          <span class="category-tag" style={tagStyleForColor(category.color)}>{category.name}</span>
        </button>
      {/each}
      <button
        type="button"
        class="category-add-option"
        onclick={() => {
          categoryNewMode = true;
          categoryNewName = '';
        }}
      >
        <span class="add-icon" aria-hidden="true">+</span>
        Add new category…
      </button>
    {:else}
      <div class="category-new-form">
        <input
          autocomplete="off"
          value={categoryNewName}
          oninput={(event) => (categoryNewName = (event.currentTarget as HTMLInputElement).value)}
          maxlength="100"
          placeholder="Category name"
          aria-label="New category name"
        />
        <div class="category-swatches" role="group" aria-label="Category color">
          {#each CATEGORY_COLORS as color (color)}
            <button
              type="button"
              class="category-swatch"
              class:selected={categoryNewColor === color}
              style={swatchStyle(color)}
              aria-label={`Color ${colorLabel(color)}`}
              aria-pressed={categoryNewColor === color}
              onmouseenter={(event) => showColorTooltip(colorLabel(color), event.currentTarget)}
              onmouseleave={hideColorTooltip}
              onclick={() => (categoryNewColor = color)}
            ></button>
          {/each}
        </div>
        <div class="category-new-actions">
          <button type="button" class="quiet" onclick={() => (categoryNewMode = false)}>Back</button
          >
          <button
            type="button"
            class="primary"
            disabled={!categoryNewName.trim() || categorySaving}
            onclick={createCategoryFromPicker}>Add category</button
          >
        </div>
      </div>
    {/if}
  </div>
{/if}

{#if manageCategoriesOpen}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_interactive_supports_focus -->
  <div
    class="modal-backdrop"
    class:closing={manageCategoriesModal.closing}
    bind:this={manageCategoriesModal.overlayEl}
    onclick={(event) => {
      if (event.target === event.currentTarget) closeManageCategories();
    }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="manage-categories-title"
    tabindex="-1"
  >
    <div class="modal form-modal manage-modal">
      <div class="modal-header">
        <h2 id="manage-categories-title">Manage categories</h2>
        <button type="button" class="modal-close" onclick={closeManageCategories} aria-label="Close"
          >×</button
        >
      </div>
      <form
        class="modal-body"
        onsubmit={(event) => {
          event.preventDefault();
          saveManageCategories();
        }}
      >
        <p class="modal-subtext">
          Categories group habits on the board. Renaming or recoloring updates every row; deleting a
          category leaves its habits uncategorized. Tagline and on-track threshold stay unique to
          habits. Changes apply when you save.
        </p>
        <div class="category-manage-list">
          {#each manageCategoryDrafts as category, index (category.id)}
            <div
              class="category-manage-row"
              role="group"
              aria-label={category.name}
              class:dragging={draggedCategoryId === category.id}
              ondragover={(event) => event.preventDefault()}
              ondrop={(event) => {
                event.preventDefault();
                moveDraftCategory(category.id);
              }}
            >
              <!-- Touch / phone: ⋯ → Move up / Move down instead of the drag handle -->
              {#if $tapAlternatives}
                {#if manageCategoryDrafts.length > 1}
                  <button
                    type="button"
                    class="row-menu-btn"
                    aria-label={`Reorder ${category.name}`}
                    onclick={() => openDraftCategorySheet(index)}
                    ><Icon name="more" size={18} /></button
                  >
                {/if}
              {:else}
                <button
                  type="button"
                  class="drag-handle"
                  draggable="true"
                  aria-label={`Drag to reorder ${category.name}`}
                  title="Drag to reorder"
                  ondragstart={(event) => {
                    draggedCategoryId = category.id;
                    event.dataTransfer?.setData('text/plain', category.id);
                    setCategoryDragImage(event, category.name, category.color);
                  }}
                  ondragend={() => (draggedCategoryId = null)}>⠿</button
                >
              {/if}
              <button
                type="button"
                class="category-dot-btn"
                aria-label={`Color for ${category.name}`}
                aria-haspopup="dialog"
                aria-expanded={manageColorPopoverId === category.id}
                onmouseenter={(event) =>
                  showColorTooltip(colorLabel(category.color), event.currentTarget)}
                onmouseleave={hideColorTooltip}
                onclick={(event) =>
                  manageColorPopoverId === category.id
                    ? closeManageColorPopover()
                    : openManageColorPopover(category.id, event.currentTarget as HTMLElement)}
              >
                <span class="category-dot" style={swatchStyle(category.color)}></span>
              </button>
              <div class="category-manage-fields">
                <input
                  autocomplete="off"
                  bind:value={category.name}
                  maxlength="100"
                  aria-label="Category name"
                />
                <div class="category-manage-meta">
                  <input
                    autocomplete="off"
                    value={category.tagline || ''}
                    oninput={(event) =>
                      (category.tagline = (event.currentTarget as HTMLInputElement).value)}
                    maxlength="160"
                    placeholder="Tagline (optional)"
                    aria-label={`Tagline for ${category.name}`}
                  />
                  <label class="threshold-field">
                    <span>On-track</span>
                    <select
                      bind:value={category.completionThresholdPercent}
                      aria-label={`On-track threshold for ${category.name}`}
                    >
                      {#each THRESHOLD_OPTIONS as value}
                        <option {value}>{value}%</option>
                      {/each}
                    </select>
                  </label>
                </div>
              </div>
              <button
                type="button"
                class="danger-link manage-action-btn"
                disabled={manageCategorySaving}
                onclick={() => requestRemoveManageCategory(category)}>Delete</button
              >
            </div>
          {/each}
        </div>
        <div class="category-manage-add">
          <button
            type="button"
            class="category-dot-btn"
            aria-label="Choose a color for the new category"
            aria-haspopup="dialog"
            aria-expanded={manageColorPopoverId === 'new'}
            onmouseenter={(event) =>
              showColorTooltip(
                newManageCategoryColor ? colorLabel(newManageCategoryColor) : 'Pick a color',
                event.currentTarget,
              )}
            onmouseleave={hideColorTooltip}
            onclick={(event) =>
              manageColorPopoverId === 'new'
                ? closeManageColorPopover()
                : openManageColorPopover('new', event.currentTarget as HTMLElement)}
          >
            <span
              class="category-dot"
              class:placeholder={!newManageCategoryColor}
              style={newManageCategoryColor ? swatchStyle(newManageCategoryColor) : undefined}
            ></span>
          </button>
          <div class="category-manage-fields">
            <input
              autocomplete="off"
              bind:value={newManageCategoryName}
              maxlength="100"
              placeholder="New category name"
              aria-label="New category name"
            />
            <div class="category-manage-meta">
              <input
                autocomplete="off"
                bind:value={newManageCategoryTagline}
                maxlength="160"
                placeholder="Tagline (optional)"
                aria-label="New category tagline"
              />
              <label class="threshold-field">
                <span>On-track</span>
                <select
                  bind:value={newManageCategoryThreshold}
                  aria-label="New category on-track threshold"
                >
                  {#each THRESHOLD_OPTIONS as value}
                    <option {value}>{value}%</option>
                  {/each}
                </select>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="quiet" onclick={closeManageCategories}>Cancel</button>
          <button
            type="submit"
            class="primary"
            disabled={!hasPersistableManageChanges ||
              manageCategorySaving ||
              manageCategoryDrafts.some((draft) => !draft.name.trim())}>Save categories</button
          >
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- svelte-ignore a11y_click_events_have_key_events a11y_interactive_supports_focus -->
{#if viewSettingsOpen}
  <div
    class="modal-backdrop"
    class:closing={viewSettingsModal.closing}
    bind:this={viewSettingsModal.overlayEl}
    onclick={(event) => {
      if (event.target === event.currentTarget) closeViewSettings();
    }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="view-settings-title"
    tabindex="-1"
  >
    <div class="modal form-modal">
      <div class="modal-header">
        <h2 id="view-settings-title">View settings</h2>
        <button type="button" class="modal-close" onclick={closeViewSettings} aria-label="Close"
          >×</button
        >
      </div>
      <div class="modal-body">
        <p class="modal-subtext">Choose how progress and category groups appear on the board.</p>
        <label class="view-setting">
          <input
            type="checkbox"
            checked={$habitsViewSettings.showStatusIcons}
            onchange={(event) =>
              updateHabitsViewSettings({
                showStatusIcons: (event.currentTarget as HTMLInputElement).checked,
              })}
          />
          <span>Show checkmark and missed icons</span>
        </label>
        <label class="view-setting">
          <input
            type="checkbox"
            checked={$habitsViewSettings.useCategoryColor}
            onchange={(event) =>
              updateHabitsViewSettings({
                useCategoryColor: (event.currentTarget as HTMLInputElement).checked,
              })}
          />
          <span>Color completed cells by category</span>
        </label>
        <label class="view-setting view-setting-select">
          <span>Category headers</span>
          <select
            aria-label="Category header display"
            value={$habitsViewSettings.categoryHeaderStyle}
            onchange={(event) =>
              updateHabitsViewSettings({
                categoryHeaderStyle: (event.currentTarget as HTMLSelectElement).value as
                  'normal' | 'compact',
              })}
          >
            <option value="normal">Normal</option>
            <option value="compact">Compact</option>
          </select>
        </label>
        <div class="modal-actions">
          <button type="button" class="primary" onclick={closeViewSettings}>Done</button>
        </div>
      </div>
    </div>
  </div>
{/if}

{#if manageColorPopoverId !== null}
  <div
    class="category-menu-popover color-picker-popover"
    bind:this={manageColorEl}
    style={manageColorStyle}
    role="dialog"
    aria-label="Choose a color"
  >
    <div class="category-swatches" role="group" aria-label="Category color">
      {#each CATEGORY_COLORS as color (color)}
        <button
          type="button"
          class="category-swatch"
          class:selected={manageColorPopoverSelected === color}
          style={swatchStyle(color)}
          aria-label={`Color ${colorLabel(color)}`}
          onmouseenter={(event) => showColorTooltip(colorLabel(color), event.currentTarget)}
          onmouseleave={hideColorTooltip}
          onclick={() => pickManageColor(color)}
        ></button>
      {/each}
    </div>
    <label class="custom-color">
      Custom
      <input
        type="color"
        value={manageColorPopoverSelected || DEFAULT_CATEGORY_COLOR}
        aria-label="Custom category color"
        oninput={(event) => pickManageColor((event.currentTarget as HTMLInputElement).value)}
      />
    </label>
  </div>
{/if}

{#if colorTooltipText && colorTooltipPosition}
  <span
    class="cat-tooltip"
    style={`left: ${colorTooltipPosition.x}px; top: ${colorTooltipPosition.y}px`}
    role="tooltip">{colorTooltipText}</span
  >
{/if}

{#if discardConfirmation}
  <div
    class="modal-backdrop confirmation-backdrop"
    class:closing={discardModal.closing}
    bind:this={discardModal.overlayEl}
    role="presentation"
  >
    <div
      class="modal confirmation-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="discard-title"
    >
      <h2 id="discard-title">Discard unsaved changes?</h2>
      <p>
        {discardConfirmation === 'categories'
          ? 'Your category edits will be lost.'
          : 'Your changes will be lost.'}
      </p>
      <div class="modal-actions">
        <button type="button" class="quiet" onclick={() => discardModal.animateClose()}
          >Keep editing</button
        >
        <button type="button" class="danger" onclick={discardChanges}>Discard changes</button>
      </div>
    </div>
  </div>
{/if}
{#if archiveConfirmation}
  <div
    class="modal-backdrop confirmation-backdrop"
    class:closing={archiveModal.closing}
    bind:this={archiveModal.overlayEl}
    role="presentation"
  >
    <div
      class="modal confirmation-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="archive-title"
    >
      <h2 id="archive-title">Archive habit?</h2>
      <p>Archive <strong>{archiveConfirmation.title}</strong>? You can restore it later.</p>
      <div class="modal-actions">
        <button type="button" class="quiet" onclick={() => archiveModal.animateClose()}
          >Cancel</button
        >
        <button type="button" class="primary" onclick={confirmArchive}>Archive habit</button>
      </div>
    </div>
  </div>
{/if}
{#if deleteConfirmation}
  <div
    class="modal-backdrop confirmation-backdrop"
    class:closing={deleteModal.closing}
    bind:this={deleteModal.overlayEl}
    role="presentation"
  >
    <div
      class="modal confirmation-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-title"
    >
      <h2 id="delete-title">Delete habit?</h2>
      <p>
        Delete <strong>{deleteConfirmation.title}</strong> and its history? This cannot be undone.
      </p>
      <div class="modal-actions">
        <button type="button" class="quiet" onclick={() => deleteModal.animateClose()}
          >Cancel</button
        >
        <button type="button" class="danger" onclick={confirmDeleteHabit}>Delete habit</button>
      </div>
    </div>
  </div>
{/if}
{#if categoryDeleteConfirmation}
  <div
    class="modal-backdrop confirmation-backdrop"
    class:closing={categoryDeleteModal.closing}
    bind:this={categoryDeleteModal.overlayEl}
    role="presentation"
  >
    <div
      class="modal confirmation-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-category-title"
    >
      <h2 id="delete-category-title">Delete category?</h2>
      <p>
        Delete <strong>{categoryDeleteConfirmation.name}</strong>? Its habits will become
        uncategorized.
      </p>
      <div class="modal-actions">
        <button type="button" class="quiet" onclick={() => categoryDeleteModal.animateClose()}
          >Cancel</button
        >
        <button type="button" class="danger" onclick={confirmDeleteCategory}>Delete category</button
        >
      </div>
    </div>
  </div>
{/if}

{#if $tapAlternatives}
  <!-- Above the page's modals: it also opens from Manage categories -->
  <ActionSheet
    --mobile-sheet-z="300"
    open={rowSheetOpen}
    onClose={() => (rowSheetOpen = false)}
    title={rowSheet?.title}
    actions={rowSheet?.actions ?? []}
  />
{/if}

<style>
  :global(body) {
    background: #f8fafc;
    color: #172033;
  }
  .page {
    min-height: 100vh;
  }
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 1rem;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
  }
  .header-left,
  .header-right,
  :global(.logo) {
    display: flex;
    align-items: center;
  }
  .header-left {
    gap: 0.75rem;
  }
  :global(.logo) {
    gap: 0.375rem;
    color: #1f2937;
    text-decoration: none;
  }
  .logo-icon {
    width: 1.25rem;
    height: 1.25rem;
  }
  .logo-text {
    font-size: 0.8125rem;
    font-weight: 700;
  }
  .page-body {
    max-width: 78rem;
    margin: 0 auto;
    padding: 1.5rem 1rem 4rem;
  }
  .page-intro {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: flex-start;
  }
  .page-intro h1 {
    margin: 0;
    color: #111827;
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.3;
  }
  .hero-copy {
    max-width: 45rem;
    margin: 0.25rem 0 0;
    color: #6b7280;
    font-size: 0.75rem;
    line-height: 1.5;
  }
  .actions,
  .toolbar,
  .modal-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .page-intro .actions {
    gap: 0.5rem;
  }
  .settings-menu-wrap {
    position: relative;
  }
  .settings-trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }
  .settings-chevron {
    width: 0.625rem;
    height: 0.375rem;
    margin-left: 0.125rem;
  }
  .settings-menu {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 10;
    min-width: 11rem;
    padding: 0.25rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity 0.15s ease,
      visibility 0.15s ease;
  }
  .settings-menu::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    left: 0;
    right: 0;
    height: 0.5rem;
  }
  .settings-menu-wrap:hover .settings-menu,
  .settings-menu.open {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
  .settings-menu button {
    display: block;
    width: 100%;
    padding: 0.5rem 0.625rem;
    color: #374151;
    text-align: left;
    background: transparent;
    border: 0;
    border-radius: 0.25rem;
    font-size: 0.75rem;
  }
  .settings-menu button:hover {
    background: #f3f4f6;
  }
  .primary,
  .quiet,
  .danger,
  .danger-link {
    padding: 0.375rem 0.75rem;
    border: 1px solid transparent;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.15s;
  }
  .primary {
    color: white;
    background: #3b82f6;
  }
  .primary:hover:not(:disabled) {
    background: #2563eb;
  }
  .primary:disabled {
    cursor: wait;
    opacity: 0.6;
  }
  .quiet {
    color: #374151;
    background: white;
    border-color: #d1d5db;
  }
  .quiet:hover {
    background: #f3f4f6;
  }
  .danger {
    color: white;
    background: #b91c1c;
    border-color: #991b1b;
  }
  .danger:hover:not(:disabled) {
    background: #991b1b;
  }
  .danger-link {
    color: #b91c1c;
    background: transparent;
  }
  .danger-link:hover:not(:disabled) {
    background: #fef2f2;
  }
  .danger-link:disabled {
    cursor: wait;
    opacity: 0.6;
  }
  .toolbar {
    margin: 28px 0 16px;
    padding: 10px 10px 16px;
    background: #fff;
    border: 1px solid #e7eaf0;
    border-radius: 10px;
  }
  .archived {
    margin-left: auto;
    font-size: 14px;
    color: #64748b;
  }
  .board-wrap {
    overflow: clip;
    background: #fff;
    border: 1px solid #e7eaf0;
    border-radius: 12px;
  }
  .sticky-head {
    position: sticky;
    top: 0;
    z-index: 2;
  }
  .board-scroll {
    overflow-x: auto;
  }
  .board {
    min-width: 1100px;
  }
  .grid {
    display: grid;
    grid-template-columns: 260px repeat(var(--date-columns, 14), minmax(55px, 1fr));
  }
  .head {
    min-width: 1100px;
    background: #fff;
    border-bottom: 1px solid #e7eaf0;
  }
  .habit-head,
  .date {
    padding: 11px 12px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
  }
  .date {
    display: grid;
    gap: 2px;
    min-height: 2.5rem;
    text-align: center;
  }
  .date small {
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 0;
    text-transform: none;
  }
  .date.today,
  .group-progress > div.today,
  .cell.today {
    box-sizing: border-box;
    border-left: 2px solid #647ce8;
    border-right: 2px solid #647ce8;
  }
  .date.today {
    background: #f5f7ff;
  }
  .group {
    border-bottom: 1px solid #e7eaf0;
  }
  .group-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 3.25rem;
    padding: 0.625rem 0.875rem;
    background: color-mix(in srgb, var(--group-color) 9%, white);
    border-bottom: 1px solid color-mix(in srgb, var(--group-color) 18%, white);
  }
  .group-title h2 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    color: #172033;
    font-size: 0.9375rem;
  }
  .group-color-dot {
    width: 0.625rem;
    height: 0.625rem;
    flex: none;
    border-radius: 999px;
    background: var(--group-color);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--group-color) 16%, transparent);
  }
  .group-title p {
    margin: 0.25rem 0 0 1.125rem;
    color: #64748b;
    font-size: 0.75rem;
    line-height: 1.35;
  }
  .group-title.compact {
    min-height: 2.375rem;
    padding-block: 0.375rem;
  }
  .group-tagline-inline {
    color: #64748b;
    font-size: 0.75rem;
    font-weight: 400;
  }
  .icon {
    border: 0;
    background: transparent;
    cursor: pointer;
  }
  .group-progress {
    background: #fafbfc;
    color: #64748b;
    font-size: 11px;
    border-bottom: 1px solid #f0f2f5;
  }
  .group-progress > div {
    min-height: 26px;
    padding: 6px;
    text-align: center;
  }
  .group-progress > div:first-child {
    text-align: left;
    padding-left: 14px;
  }
  .on-track {
    color: #16805c;
    font-weight: 700;
  }
  .habit-row {
    /* Fixed height: names clamp to two lines and descriptions live in the hover
       popover, so every row stays the same height regardless of name length. */
    height: 60px;
    min-height: 60px;
    grid-auto-rows: 60px;
    border-bottom: 1px solid #f0f2f5;
  }
  .habit-name {
    min-height: 0;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 9px 10px 9px 14px;
    position: relative;
  }
  .drag-handle {
    flex: none;
    align-self: center;
    padding: 0.125rem 0.25rem;
    color: #94a3b8;
    background: transparent;
    border: 0;
    border-radius: 0.25rem;
    cursor: grab;
    font-size: 1rem;
    line-height: 1;
  }
  .drag-handle:active {
    cursor: grabbing;
  }
  /* Touch / phone stand-in for .drag-handle: ⋯ → Move up / Move down (32px wide, 44px hit area) */
  .row-menu-btn {
    position: relative;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: var(--mobile-tap);
    margin-left: -0.5rem;
    padding: 0;
    color: #64748b;
    background: transparent;
    border: 0;
    border-radius: 0.375rem;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .row-menu-btn::after {
    content: '';
    position: absolute;
    inset: 0 -6px;
  }
  .row-menu-btn:active {
    background: #f1f5f9;
  }
  .dragging {
    opacity: 0.5;
  }
  .habit-details {
    flex: 1;
    min-width: 0;
  }
  .habit-edit {
    border: 0;
    border-radius: 4px;
    background: none;
    padding: 1px 3px;
    color: #172033;
    font: 600 14px inherit;
    line-height: 1.35;
    cursor: pointer;
    text-align: left;
    /* Clamp to two lines so rows stay a consistent height; the full name is
       available in the hover popover. */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    overflow-wrap: break-word;
  }
  .habit-edit:hover,
  .habit-edit:focus-visible {
    background: #eef2ff;
    color: #3730a3;
    outline: none;
  }
  /* Hover popover with the full habit name + description, anchored below the row. */
  .habit-info {
    position: absolute;
    top: calc(100% + 6px);
    left: 14px;
    right: 14px;
    z-index: 4;
    min-width: 180px;
    max-height: 240px;
    overflow-y: auto;
    box-sizing: border-box;
    padding: 10px 12px;
    border: 1px solid #374151;
    border-radius: 8px;
    background: #1f2937;
    color: #fff;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.15);
    word-break: break-word;
    animation: info-in 120ms ease-out;
  }
  /* Near the bottom of the board: flip the popover above the row so it stays
     inside the `.board-wrap` container instead of overflowing it. */
  .habit-info.above {
    top: auto;
    bottom: calc(100% + 6px);
    animation: info-in-above 120ms ease-out;
  }
  /* First render: keep the popover laid out in place (left/right still apply,
     so content wraps at its real width) but shift it far offscreen with a
     transform so it can be measured against the container without being seen
     or contributing overflow. A transform doesn't change the used width or
     height, and the translated box is what counts for scrollable overflow. */
  .habit-info.measuring {
    transform: translate(-9999px, -9999px);
    visibility: hidden;
    pointer-events: none;
    animation: none;
  }
  .habit-info-title {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    white-space: pre-wrap;
  }
  .habit-info-description {
    margin-top: 6px;
    color: #d1d5db;
    font-size: 12px;
    line-height: 1.45;
    white-space: pre-wrap;
  }
  @keyframes info-in {
    from {
      opacity: 0;
      transform: translateY(-2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes info-in-above {
    from {
      opacity: 0;
      transform: translateY(2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .cell {
    min-height: 0;
    border-left: 1px solid #f0f2f5;
    display: grid;
    place-items: center;
  }
  .cell button {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    min-height: 0;
    border: 0;
    background: transparent;
    color: #334155;
    cursor: pointer;
    overflow: hidden;
    display: grid;
    place-items: center;
    position: relative;
  }
  .cell-icon {
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(80%, 40px);
    height: min(80%, 40px);
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2.5;
  }
  .cell-icon-check {
    color: color-mix(in srgb, var(--completed-color) 45%, white);
  }
  .cell-icon-missed {
    color: #a14a4a;
  }
  .cell-label {
    display: -webkit-box;
    padding: 0 3px;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    line-height: 1.2;
    font-size: 11px;
    font-weight: 600;
    opacity: 0;
    transition: opacity 120ms ease;
  }
  .cell button:hover:not(:disabled) .cell-icon,
  .cell button:focus-visible .cell-icon {
    display: none;
  }
  .cell button:hover:not(:disabled),
  .cell button:focus-visible {
    background: color-mix(in srgb, var(--cell-color) 18%, white);
    outline: none;
  }
  .cell button:hover:not(:disabled) .cell-label,
  .cell button:focus-visible .cell-label {
    opacity: 1;
  }
  .cell button.completed {
    background: var(--completed-color);
    color: #fff;
  }
  .cell button.completed:hover:not(:disabled),
  .cell button.completed:focus-visible {
    background: color-mix(in srgb, var(--completed-color) 82%, #172033);
  }
  .cell button.missed {
    background: #f8eaea;
    color: #a14a4a;
  }
  .cell button.disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }
  .empty {
    text-align: center;
    padding: 65px;
    color: #64748b;
  }
  .empty h2 {
    color: #172033;
  }
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: #17203388;
    /* Sit above the Now bar (which uses z-index 80), matching modal layering elsewhere. */
    z-index: 100;
    display: grid;
    place-items: center;
    padding: 20px;
    animation: overlay-in 150ms ease-out both;
  }
  .modal-backdrop.closing {
    animation: overlay-out 100ms ease-in both;
  }
  @keyframes overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  @keyframes overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
  .modal {
    background: #fff;
    width: min(440px, 100%);
    border-radius: 14px;
    padding: 24px;
    box-shadow: 0 20px 50px #0004;
  }
  .modal h2 {
    margin: 0 0 18px;
  }
  .form-modal {
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 2rem);
  }
  .form-modal h2 {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #111827;
  }
  .manage-modal {
    width: min(32rem, 100%);
  }
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
  }
  .modal-close {
    background: transparent;
    border: none;
    color: #9ca3af;
    padding: 0.25rem 0.4rem;
    border-radius: 0.375rem;
    font-size: 1.125rem;
    line-height: 1;
    cursor: pointer;
  }
  .modal-close:hover {
    color: #4b5563;
    background: #f3f4f6;
  }
  .modal-body {
    padding: 1.125rem 1.25rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    overflow-y: auto;
  }
  .modal-subtext {
    margin: 0;
    color: #6b7280;
    font-size: 0.6875rem;
    line-height: 1.45;
  }
  .view-setting {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: #374151;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .modal-field {
    display: grid;
    gap: 0.3125rem;
    margin: 0;
    color: #374151;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .modal-field .field-hint {
    font-weight: 400;
    color: #64748b;
  }
  .confirmation-backdrop {
    z-index: 110;
  }
  .confirmation-modal p {
    margin: 0;
    color: #64748b;
    line-height: 1.5;
  }
  .modal label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    margin: 12px 0;
  }
  .modal label span {
    font-weight: 400;
    color: #64748b;
  }
  .modal input:not([type='color']):not([type='checkbox']),
  .modal textarea,
  .modal select {
    box-sizing: border-box;
    width: 100%;
    margin-top: 5px;
    padding: 9px;
    border: 1px solid #cbd5e1;
    border-radius: 7px;
    font: inherit;
  }
  .modal .view-setting-select {
    display: grid;
    gap: 0.25rem;
    margin-block: 0;
  }
  .view-setting-select select {
    margin-top: 0;
    padding: 0.375rem 0.5rem;
    font-size: 0.75rem;
  }
  .view-setting input[type='checkbox'] {
    width: 0.8125rem;
    height: 0.8125rem;
    margin: 0;
    flex-shrink: 0;
    accent-color: #3b82f6;
  }
  .modal textarea {
    min-height: 75px;
    resize: vertical;
  }
  .modal-actions {
    justify-content: flex-end;
    margin-top: 0.5rem;
  }
  .form-modal .modal-actions {
    margin-top: 0.25rem;
  }
  .habit-management-actions {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
  }
  .category-picker-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 0.625rem;
    color: #1f2937;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 400;
    text-align: left;
    cursor: pointer;
  }
  .category-picker-trigger:hover {
    border-color: #9ca3af;
  }
  .category-picker-trigger:focus-visible {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  }
  .status-caret {
    color: #9ca3af;
    font-size: 0.75rem;
  }
  .category-tag {
    display: inline-block;
    padding: 0.0625rem 0.375rem;
    color: var(--cat-text, #475569);
    background: var(--cat-bg, #f1f5f9);
    box-shadow: inset 0 0 0 1px var(--cat-border, transparent);
    border-radius: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 500;
  }
  .category-menu-popover {
    position: fixed;
    z-index: 120;
    min-width: 14rem;
    max-width: 16rem;
    max-height: 18rem;
    overflow-y: auto;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    padding: 0.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
  }
  .category-option,
  .category-add-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.4375rem 0.625rem;
    border: none;
    background: transparent;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #374151;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
  }
  .category-option:hover,
  .category-add-option:hover {
    background: #f3f4f6;
  }
  .category-option.active {
    background: #f9fafb;
  }
  .category-option .category-tag {
    pointer-events: none;
  }
  .category-add-option {
    color: #2563eb;
    font-weight: 600;
    border-top: 1px solid #f3f4f6;
    border-radius: 0;
    margin-top: 0.125rem;
  }
  .category-add-option:hover {
    background: #eff6ff;
  }
  .add-icon {
    font-size: 0.8125rem;
    font-weight: 700;
  }
  .category-new-form {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.375rem;
  }
  .category-new-form input {
    padding: 0.4375rem 0.625rem;
    color: #1f2937;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 400;
  }
  .category-new-form input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  }
  .category-swatches {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
  }
  .category-swatch {
    width: 1.375rem;
    height: 1.375rem;
    border-radius: 999px;
    border: 2px solid transparent;
    cursor: pointer;
    transition: transform 0.1s;
  }
  .category-swatch:hover {
    transform: scale(1.1);
  }
  .category-swatch.selected {
    border-color: #111827;
    box-shadow: inset 0 0 0 2px white;
  }
  .category-new-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.375rem;
  }
  .category-manage-list {
    display: grid;
    gap: 0.625rem;
  }
  .category-manage-row {
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    align-items: start;
    gap: 0.5rem;
  }
  .category-manage-add {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: start;
    gap: 0.5rem;
    margin-top: 0.25rem;
    padding-top: 0.75rem;
    border-top: 1px solid #e5e7eb;
  }
  .category-manage-fields {
    display: grid;
    gap: 0.375rem;
  }
  .category-manage-meta {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.375rem;
    align-items: center;
  }
  .modal .category-manage-row input,
  .modal .category-manage-add input,
  .modal .category-manage-row select,
  .modal .category-manage-add select {
    width: 100%;
    margin: 0;
    padding: 0.375rem 0.5rem;
    color: #1f2937;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 400;
  }
  .modal .category-manage-row input:focus,
  .modal .category-manage-add input:focus,
  .modal .category-manage-row select:focus,
  .modal .category-manage-add select:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  }
  .modal .threshold-field {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin: 0;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #6b7280;
    white-space: nowrap;
  }
  .threshold-field select {
    min-width: 4.25rem;
  }
  .manage-action-btn {
    min-width: 3.75rem;
    text-align: center;
    margin-top: 0.125rem;
  }
  .category-dot-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    margin-top: 0.2rem;
    padding: 0;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    cursor: pointer;
  }
  .category-dot-btn:hover,
  .category-dot-btn:focus-visible {
    outline: none;
    border-color: #3b82f6;
  }
  .category-dot {
    display: block;
    width: 0.875rem;
    height: 0.875rem;
    border-radius: 999px;
  }
  .category-dot.placeholder {
    background: transparent;
    border: 1.5px dashed #9ca3af;
    box-sizing: border-box;
  }
  .color-picker-popover {
    min-width: 0;
    width: max-content;
    max-width: none;
    padding: 0.5rem;
  }
  .color-picker-popover .category-swatches {
    display: grid;
    grid-template-columns: repeat(5, auto);
    gap: 0.5rem;
  }
  .custom-color {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0.5rem 0 0;
    padding-top: 0.5rem;
    border-top: 1px solid #e5e7eb;
    color: #6b7280;
    font-size: 0.6875rem;
    font-weight: 600;
  }
  .custom-color input[type='color'] {
    width: 30px;
    height: 26px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
  }
  .cat-tooltip {
    position: fixed;
    z-index: 200;
    transform: translate(-50%, -100%);
    padding: 0.25rem 0.5rem;
    background: #1f2937;
    color: white;
    border-radius: 0.375rem;
    font-size: 0.625rem;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    pointer-events: none;
    white-space: nowrap;
  }
  @media (min-width: 768px) and (max-width: 1023px) {
    .board,
    .head {
      min-width: 0;
    }
    .grid {
      grid-template-columns: minmax(220px, 2.5fr) repeat(var(--date-columns), minmax(55px, 1fr));
    }
  }
  @media (max-width: 767px) {
    .page-body {
      padding: 0.75rem 0.875rem 1.25rem;
    }
    /* One 44px ‹ label › Today row; Show archived is in the ⚙ sheet. */
    .toolbar {
      margin: 0 0 10px;
      padding: 0;
      border-radius: 12px;
    }
    .board-wrap {
      overflow: visible;
    }
    .sticky-head {
      position: static;
    }
    .board-scroll {
      overflow: visible;
    }
    .board,
    .head {
      min-width: 0;
    }
    /* 140px leaves the five day columns 44px+ each at 393px. The cell dividers
       are inset shadows instead of borders, so the button fills its column. */
    .grid {
      grid-template-columns: minmax(140px, 2.5fr) repeat(var(--date-columns), minmax(28px, 1fr));
    }
    .cell,
    .cell button {
      border-left: 0;
      box-shadow: inset 1px 0 0 #f0f2f5;
    }
    .cell.today,
    .cell.today button {
      border-left: 0;
      border-right: 0;
      box-shadow:
        inset 2px 0 0 #647ce8,
        inset -2px 0 0 #647ce8;
    }
    .habit-head,
    .date {
      padding: 0.5rem 0.125rem;
    }
    .habit-head {
      padding-left: 0.625rem;
      font-size: 10px;
    }
    .date {
      min-height: 3.25rem;
    }
    .group-progress > div:first-child {
      padding-left: 0.625rem;
      font-size: 10px;
    }
    .habit-row {
      height: 3.75rem;
      min-height: 3.75rem;
      grid-auto-rows: 3.75rem;
    }
    .habit-name {
      padding: 0.35rem 0.25rem 0.35rem 0.625rem;
      gap: 0.25rem;
    }
    .habit-edit {
      -webkit-line-clamp: 2;
      line-clamp: 2;
      font-size: 16px;
    }
    /* Tap targets (plan §3.7): the name button fills the cell's height and
       width (the line clamp clips an ::after, so it can't grow one) and keeps
       the name centered; the ⚙ grows a transparent hit area. */
    .habit-details {
      display: flex;
      align-self: stretch;
    }
    .habit-edit {
      flex: 1;
      min-width: 0;
      -webkit-box-pack: center;
    }
    .group-title .icon {
      position: relative;
    }
    .group-title .icon::after {
      content: '';
      position: absolute;
      inset: -10px -8px -10px -24px;
    }
    .habit-row .cell {
      min-height: 0;
    }
  }
</style>
